import {
    Component,
    computed,
    EventEmitter,
    Input,
    Output,
    SecurityContext,
    signal,
    ViewChild,
    inject,
    OnInit,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IonModal } from '@ionic/angular';
import { IonicModule } from '@ionic/angular/lazy';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../input/input.component';

export type Item = {
    text: string;
    value: string;
};

@Component({
    selector: 'web-modal-item-picker-component',
    imports: [IonicModule, TranslateModule, InputComponent],
    templateUrl: './modal-item-picker.component.html',
    styleUrl: './modal-item-picker.component.scss',
})
export class ModalItemPickerComponentComponent implements OnInit {
    private sanitizer = inject(DomSanitizer);

    @ViewChild('modal', { static: true }) modal!: IonModal;

    @Input() field = '';
    @Input() items: Item[] = [];
    @Input() selectedItems: object | string | number = '';
    @Input() title = 'Select Items';
    @Input() error?: string | null;
    @Input() disabled = false;

    @Output() selectionChange = new EventEmitter<string>();

    filteredItems: Item[] = [];
    workingSelectedValue = signal<string>('');

    get sanitizedTitle(): SafeHtml {
        const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, this.title);
        return this.sanitizer.bypassSecurityTrustHtml(sanitized || '');
    }

    buttonText = computed(() => {
        const values = this.workingSelectedValue();
        return values ? this.items.find(item => item.value === values)?.text : '';
    });

    ngOnInit() {
        this.filteredItems = [...this.items];
        if (typeof this.selectedItems === 'object') {
            this.selectedItems = JSON.stringify(this.selectedItems);
        }
        this.workingSelectedValue.set(this.selectedItems.toString());
    }

    cancelChanges() {
        this.modal.dismiss();
    }

    confirmChanges() {
        this.selectionChange.emit(this.workingSelectedValue());
        this.modal.dismiss();
    }

    searchbarInput(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.filterList(inputElement.value);
    }

    /**
     * Update the rendered view with
     * the provided search query. If no
     * query is provided, all data
     * will be rendered.
     */
    filterList(searchQuery: string | undefined) {
        /**
         * If no search query is defined,
         * return all options.
         */
        if (searchQuery === undefined || searchQuery.trim() === '') {
            this.filteredItems = [...this.items];
        } else {
            /**
             * Otherwise, normalize the search
             * query and check to see which items
             * contain the search query as a substring.
             */
            const normalizedQuery = searchQuery.toLowerCase();
            this.filteredItems = this.items.filter(item => item.text.toLowerCase().includes(normalizedQuery));
        }
    }

    isChecked(value: string): boolean {
        return this.workingSelectedValue() === value;
    }

    radioChange(event: CustomEvent<{ value: string }>) {
        const { value } = event.detail;
        this.workingSelectedValue.set(value);
        this.confirmChanges();
    }
}
